import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { draftManager } from '../lib/draftManager.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { formatDateTime } from '../lib/dateUtils';
import type { DraftEntityType, UserDraft } from '@gng-ehs/shared';

interface SectionDraftsViewProps {
  entityType: DraftEntityType;
  sectionTitle: string;
  onResume: (draft: UserDraft) => void;
}

function formatRelativeTime(dateStr: string): string {
  try {
    const time = new Date(dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`).getTime();
    const diffSec = Math.floor((Date.now() - time) / 1000);
    if (diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDateTime(dateStr);
  } catch {
    return 'Recently';
  }
}

export function SectionDraftsView({
  entityType,
  sectionTitle,
  onResume,
}: SectionDraftsViewProps) {
  const [drafts, setDrafts] = useState<UserDraft[]>(() => draftManager.listDrafts(entityType));
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [deleteItem, setDeleteItem] = useState<UserDraft | null>(null);
  const [clearAllOpen, setClearAllOpen] = useState(false);

  const refreshDrafts = useCallback(() => {
    setDrafts(draftManager.listDrafts(entityType));
  }, [entityType]);

  useEffect(() => {
    refreshDrafts();
    const unsub = draftManager.subscribe(() => refreshDrafts());
    return unsub;
  }, [refreshDrafts]);

  const toggleDetails = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDiscard = (draft: UserDraft) => {
    draftManager.deleteDraft(draft.id);
    setDeleteItem(null);
    refreshDrafts();
  };

  const handleClearAll = () => {
    draftManager.clearAllDrafts(entityType);
    setClearAllOpen(false);
    refreshDrafts();
  };

  if (drafts.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 sm:p-12 text-center my-2">
        <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center text-xl mx-auto mb-3 shadow-inner">
          📝
        </div>
        <h3 className="text-sm sm:text-base font-semibold text-gray-900">
          No Saved Drafts for {sectionTitle}
        </h3>
        <p className="text-xs sm:text-sm text-gray-500 mt-1 max-w-md mx-auto">
          When you start filling out a new {sectionTitle.toLowerCase()} record, any unsaved progress is automatically saved here so you never lose your work.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 my-2">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-800">
            Unsaved {sectionTitle} Drafts
          </span>
          <span className="text-xs bg-amber-100 text-amber-800 font-semibold px-2 py-0.5 rounded-full">
            {drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'}
          </span>
        </div>

        {drafts.length > 1 && (
          <button
            type="button"
            onClick={() => setClearAllOpen(true)}
            className="text-xs text-red-600 hover:text-red-700 font-medium hover:underline cursor-pointer"
          >
            Clear All Drafts
          </button>
        )}
      </div>

      <div className="space-y-2.5">
        {drafts.map((draft) => {
          const isExpanded = expandedIds.has(draft.id);
          const dataEntries = Object.entries(draft.form_data || {}).filter(
            ([k, v]) => v !== '' && v !== null && v !== undefined && k !== 'fields'
          );

          return (
            <div
              key={draft.id}
              className="bg-white rounded-xl border border-amber-200/80 hover:border-amber-300 transition-all shadow-sm overflow-hidden"
            >
              <div className="p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-base flex-shrink-0">
                    💾
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-gray-900 truncate">
                        {draft.title || 'Untitled Draft'}
                      </h4>
                      <span className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.2 rounded">
                        Saved {formatRelativeTime(draft.updated_at)}
                      </span>
                    </div>
                    {draft.subtitle && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{draft.subtitle}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => toggleDetails(draft.id)}
                    className="btn btn-secondary text-xs px-2.5 py-1.5 h-8 flex items-center gap-1 cursor-pointer font-medium"
                  >
                    <span>{isExpanded ? 'Hide Details' : 'Details'}</span>
                    <span className="text-[10px]">{isExpanded ? '▲' : '▼'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setDeleteItem(draft)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors cursor-pointer"
                    title="Discard Draft"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    onClick={() => onResume(draft)}
                    className="btn bg-amber-600 hover:bg-amber-700 text-white text-xs px-3.5 py-1.5 h-8 flex items-center gap-1.5 cursor-pointer font-semibold shadow-sm"
                  >
                    <span>Resume</span>
                    <span>→</span>
                  </button>
                </div>
              </div>

              {/* Accordion snapshot */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.16 }}
                    className="border-t border-amber-100 bg-amber-50/40 px-4 py-3"
                  >
                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Saved Form Snapshot
                    </div>
                    {dataEntries.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No field details stored yet.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {dataEntries.map(([key, val]) => (
                          <div
                            key={key}
                            className="bg-white rounded p-2 border border-gray-200 text-xs overflow-hidden"
                          >
                            <div className="text-[10px] font-medium text-gray-400 uppercase truncate">
                              {key.replace(/_/g, ' ')}
                            </div>
                            <div className="font-medium text-gray-800 truncate mt-0.5">
                              {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!deleteItem}
        title="Discard Draft?"
        message={`Are you sure you want to discard the draft for "${deleteItem?.title || 'this form'}"? This action cannot be undone.`}
        confirmLabel="Discard Draft"
        variant="danger"
        onConfirm={() => { if (deleteItem) handleDiscard(deleteItem); }}
        onCancel={() => setDeleteItem(null)}
      />

      <ConfirmDialog
        open={clearAllOpen}
        title="Clear All Drafts?"
        message={`Are you sure you want to delete all saved drafts for ${sectionTitle}?`}
        confirmLabel="Clear All"
        variant="danger"
        onConfirm={handleClearAll}
        onCancel={() => setClearAllOpen(false)}
      />
    </div>
  );
}
